import React from "react";

import {
  FiCalendar,
  FiEdit2,
  FiFile,
  FiFlag,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiTag,
  FiUser,
  FiUsers,
  FiX,
  FiClipboard,
  FiList,
} from "react-icons/fi";

import Config from "../../../../../../config";

import {
  formatRequirementDate,
  getAttachmentFileName,
  getCustomerMobile,
  getCustomerName,
  getRaisedByName,
  getRequirementPriorityClass,
  getRequirementStatusClass,
  isImageAttachment,
  isPdfAttachment,
} from "./constants";

const getAttachmentUrl = (attachment) => {
  if (!attachment) {
    return "#";
  }

  const attachmentPath = String(
    attachment
  ).trim();

  if (
    attachmentPath.startsWith(
      "http://"
    ) ||
    attachmentPath.startsWith(
      "https://"
    )
  ) {
    return attachmentPath;
  }

  return `${Config.site_api}/${attachmentPath.replace(
    /^\/+/,
    ""
  )}`;
};

const RequirementDrawer = ({
  open,
  requirement,
  onClose,
  onEdit,
  onChangeStatus,
  onOpenCustomer,
  onAssignTask,
  onViewTasks,
}) => {
  if (!open || !requirement) {
    return null;
  }

  const customer =
    requirement?.customer || {};

  const customerName =
    getCustomerName(customer);

  const customerMobile =
    getCustomerMobile(customer);

  const customerEmail =
    customer?.email || "-";

  const customerCompany =
    customer?.companyName || "-";

  const customerAddress =
    customer?.address || "-";

  const raisedByName =
    getRaisedByName(requirement);

  const attachments = Array.isArray(
    requirement?.attachments
  )
    ? requirement.attachments
    : [];

  const taskSummary =
    requirement?.taskSummary ||
    {};

  const totalTasks = Number(
    taskSummary.totalTasks || 0
  );

  const openTasks = Number(
    taskSummary.openTasks || 0
  );

  const unreadTasks = Number(
    taskSummary.unreadTaskCount ||
    0
  );

  return (
    <div className="requirement-drawer-overlay">
      <button
        type="button"
        className="requirement-drawer-backdrop"
        onClick={onClose}
        aria-label="Close requirement details"
      />

      <aside
        className="requirement-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Requirement details"
      >
        <div className="requirement-drawer-header">
          <div>
            <span>
              CUSTOMER REQUIREMENT
            </span>

            <h2>
              Requirement Details
            </h2>
          </div>

          <button
            type="button"
            className="requirement-drawer-close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <FiX />
          </button>
        </div>

        <div className="requirement-drawer-body">
          <section className="requirement-drawer-card requirement-drawer-summary">
            <div>
              <span>Status</span>

              <button
                type="button"
                className={getRequirementStatusClass(
                  requirement?.status
                )}
                onClick={() =>
                  onChangeStatus(
                    requirement
                  )
                }
              >
                {requirement?.status ||
                  "Active"}
              </button>
            </div>

            <div>
              <span>Priority</span>

              <strong
                className={getRequirementPriorityClass(
                  requirement?.priority
                )}
              >
                {requirement?.priority ||
                  "Medium"}
              </strong>
            </div>

            <div>
              <span>Raised By</span>

              <strong>
                {raisedByName}
              </strong>

              <small>
                {requirement?.raisedBy ||
                  "customer"}
              </small>
            </div>
          </section>

          <section className="requirement-drawer-card">
            <div className="requirement-drawer-card-heading">
              <h3>
                <FiUser />
                Customer Information
              </h3>

              <button
                type="button"
                className="requirement-drawer-link-button"
                onClick={() => {
                  if (
                    typeof onOpenCustomer ===
                    "function"
                  ) {
                    onOpenCustomer(
                      customer
                    );
                  }
                }}
              >
                <FiUsers />
                View Customer
              </button>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiUser />

              <div>
                <span>
                  Customer Name
                </span>

                <strong>
                  {customerName}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiPhone />

              <div>
                <span>
                  Mobile Number
                </span>

                <strong>
                  {customerMobile}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiMail />

              <div>
                <span>
                  Email Address
                </span>

                <strong>
                  {customerEmail}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiTag />

              <div>
                <span>
                  Company Name
                </span>

                <strong>
                  {customerCompany}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiMapPin />

              <div>
                <span>
                  Address
                </span>

                <strong>
                  {customerAddress}
                </strong>
              </div>
            </div>
          </section>

          <section className="requirement-drawer-card">
            <h3>
              <FiTag />
              Requirement Information
            </h3>

            <div className="requirement-drawer-detail-row">
              <FiTag />

              <div>
                <span>
                  Requirement Type
                </span>

                <strong>
                  {requirement?.requirementType ||
                    "-"}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-detail-row">
              <FiFlag />

              <div>
                <span>
                  Priority
                </span>

                <strong>
                  {requirement?.priority ||
                    "Medium"}
                </strong>
              </div>
            </div>

            <div className="requirement-drawer-description">
              <span>Description</span>

              <p>
                {requirement?.description ||
                  "No description available."}
              </p>
            </div>
          </section>

          <section className="requirement-drawer-card">
            <h3>
              <FiCalendar />
              Timeline
            </h3>

            <div className="requirement-drawer-grid">
              <div>
                <span>
                  Created On
                </span>

                <strong>
                  {formatRequirementDate(
                    requirement?.createdAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Last Updated
                </span>

                <strong>
                  {formatRequirementDate(
                    requirement?.updatedAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Completed On
                </span>

                <strong>
                  {formatRequirementDate(
                    requirement?.completedAt
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Closed On
                </span>

                <strong>
                  {formatRequirementDate(
                    requirement?.closedAt
                  )}
                </strong>
              </div>
            </div>
          </section>

          <section className="requirement-drawer-card">
            <div className="requirement-drawer-card-heading">
              <h3>
                <FiClipboard />
                Linked Tasks
              </h3>

              <button
                type="button"
                className="requirement-drawer-link-button"
                onClick={() =>
                  onAssignTask?.(
                    requirement
                  )
                }
              >
                <FiClipboard />
                Assign Task
              </button>
            </div>

            {totalTasks > 0 ? (
              <>
                <div className="requirement-drawer-task-summary">
                  <div>
                    <span>
                      Total Tasks
                    </span>

                    <strong>
                      {totalTasks}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Open Tasks
                    </span>

                    <strong>
                      {openTasks}
                    </strong>
                  </div>

                  <div
                    className={
                      unreadTasks > 0
                        ? "has-unread"
                        : ""
                    }
                  >
                    <span>
                      New Updates
                    </span>

                    <strong>
                      {unreadTasks}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="requirement-drawer-view-tasks-button"
                  onClick={() =>
                    onViewTasks?.(
                      requirement
                    )
                  }
                >
                  <FiList />
                  View All Linked Tasks
                </button>
              </>
            ) : (
              <div className="requirement-drawer-no-tasks">
                <FiClipboard />

                <div>
                  <strong>
                    No task assigned
                  </strong>

                  <span>
                    Assign this requirement to an employee for follow-up.
                  </span>
                </div>
              </div>
            )}
          </section>

          <section className="requirement-drawer-card">
            <h3>
              <FiFile />
              Attachments
            </h3>

            {attachments.length > 0 ? (
              <div className="requirement-drawer-files">
                {attachments.map(
                  (
                    attachment,
                    index
                  ) => {
                    const fileName =
                      getAttachmentFileName(
                        attachment
                      );

                    const attachmentUrl =
                      getAttachmentUrl(
                        attachment
                      );

                    return (
                      <a
                        key={`${attachment}-${index}`}
                        href={
                          attachmentUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="requirement-drawer-file"
                      >
                        <div className="requirement-drawer-file-icon">
                          {isImageAttachment(
                            attachment
                          ) ? (
                            <img
                              src={
                                attachmentUrl
                              }
                              alt={
                                fileName
                              }
                              onError={(
                                event
                              ) => {
                                event.currentTarget.style.display =
                                  "none";
                              }}
                            />
                          ) : (
                            <FiFile />
                          )}
                        </div>

                        <div className="requirement-drawer-file-info">
                          <strong
                            title={
                              fileName
                            }
                          >
                            {fileName}
                          </strong>

                          <span>
                            {isPdfAttachment(
                              attachment
                            )
                              ? "PDF document"
                              : isImageAttachment(
                                attachment
                              )
                                ? "Image file"
                                : "Attachment"}
                          </span>
                        </div>
                      </a>
                    );
                  }
                )}
              </div>
            ) : (
              <div className="requirement-drawer-no-files">
                <FiFile />

                <span>
                  No attachments available.
                </span>
              </div>
            )}
          </section>
        </div>
        <div className="requirement-drawer-footer">
          <button
            type="button"
            className="requirement-drawer-task-button"
            onClick={() =>
              onAssignTask?.(
                requirement
              )
            }
          >
            <FiClipboard />
            Assign Task
          </button>

          <button
            type="button"
            className="requirement-drawer-edit-button"
            onClick={() =>
              onEdit(requirement)
            }
          >
            <FiEdit2 />
            Edit Requirement
          </button>

          <button
            type="button"
            className="requirement-drawer-status-button"
            onClick={() =>
              onChangeStatus(
                requirement
              )
            }
          >
            <FiRefreshCw />
            Change Status
          </button>
        </div>
      </aside>
    </div>
  );
};

export default RequirementDrawer;